/**
 * Flatbox-leads och deras Flatbox-offerter (flatbox_offers.leadId).
 *
 * Ren logik (bara importer med .js-ändelse): används av leadsidan,
 * leadspanelen, Flatbox-konfiguratorn (sub-appen) och scripts/flatbox-link-leads.mjs.
 */
import { matchaFlatboxOffertKund } from './kundmatchning.js';

/** Produkten är Flatbox (samma regel som konfiguratorlänken i Produkter). */
export function isFlatboxProduct(product) {
  return /flatbox/i.test(product?.name || '');
}

/** Leadet gäller Flatbox: productId pekar på en Flatbox-produkt. */
export function isFlatboxLead(lead, products) {
  if (!lead?.productId) return false;
  return isFlatboxProduct((products || []).find(p => p.id === lead.productId));
}

/** Offertdokument (flatbox_offers) grupperade per leadId. Utan leadId hoppas över. */
export function flatboxOfferterPerLead(docs) {
  const map = {};
  for (const d of docs || []) if (d?.leadId) (map[d.leadId] ||= []).push(d);
  return map;
}

export function arSignerad(doc) {
  return !!doc?.offer?.signeringsdatum;
}

/**
 * Etikett på leadkortet: "Signerad" om någon offert är signerad, annars
 * antalet offerter. Inga offerter → null.
 */
export function flatboxEtikett(docs) {
  const n = (docs || []).length;
  if (!n) return null;
  if (docs.some(arSignerad)) return { text: 'Signerad', signerad: true };
  return { text: `${n} ${n === 1 ? 'offert' : 'offerter'}`, signerad: false };
}

export function flatboxUpplagg(config) {
  if (config?.impenza) return 'Impenza';
  return config?.deployment === 'onprem' ? 'On-prem' : 'SaaS';
}

/** Månadspriset ur priceSnapshot: prova-på-priset under kampanj, annars ordinarie. */
export function flatboxManadspris(doc) {
  const ps = doc?.priceSnapshot || {};
  if (doc?.config?.campaignActive && ps.campaignMonthly != null) return ps.campaignMonthly;
  return ps.monthly ?? null;
}

/** En rad i leadets offertlista. */
export function flatboxOffertRad(doc) {
  const off = doc?.offer || {};
  return {
    offerNo: off.offerNo || '',
    upplagg: flatboxUpplagg(doc?.config),
    kampanj: !!doc?.config?.campaignActive,
    anvandare: doc?.priceSnapshot?.tier ? `${doc.priceSnapshot.tier} anv.`
      : doc?.config?.users != null ? `${doc.config.users} anv.` : '',
    manadspris: flatboxManadspris(doc),
    datum: off.offerDate || '',
    signerad: arSignerad(doc),
    signeringsdatum: off.signeringsdatum || '',
    skickad: !!doc?.sent,
  };
}

/** Senaste först: offertdatum, därefter offertnummer. */
export function sorteraFlatboxOfferter(docs) {
  return [...(docs || [])].sort((a, b) =>
    String(b.offer?.offerDate || '').localeCompare(String(a.offer?.offerDate || ''))
    || String(b.offer?.offerNo || '').localeCompare(String(a.offer?.offerNo || ''), 'sv', { numeric: true }));
}

/** Leadets huvudkontakt, annars kundens primära, annars första. */
export function valjKontakt(contacts, mainContactId) {
  const list = contacts || [];
  return list.find(c => c.id === mainContactId) || list.find(c => c.primary) || list[0] || null;
}

// ── Kampanj (pilot/prova-på) på leadet ──
// Samma datumregel som konfiguratorn (flatbox/kampanj.js): slut = leveransdatum
// + campaignMonths − 1 dag. Dupliceras här så att modulen saknar importer.
const kampanjSlutDatum = (leverans, manader) => {
  const p = String(leverans || '').split('-');
  const d = new Date(Number(p[0]), Number(p[1]) - 1, Number(p[2]) || 1, 12);
  d.setMonth(d.getMonth() + Number(manader));
  d.setDate(d.getDate() - 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

/** Är offerten en kampanj (pilot eller prova-på)? Nya modellen eller äldre SaaS-projektkampanj. */
export function arKampanjoffert(doc) {
  const c = doc?.config || {};
  return c.deployment === 'saas' && !!c.campaignActive && (c.campaignModel === 2 || c.scope === 'project');
}

/**
 * Leadets kampanj: kampanjofferten med leveransdatum och senast slutdatum.
 * → { offerNo, slut, pilot } | null. pilot = 100 % rabatt.
 */
export function kampanjForLead(docs) {
  const kandidater = (docs || []).filter(arKampanjoffert).filter(d => d.offer?.planeradLeveransdag)
    .map(d => ({
      offerNo: d.offer.offerNo,
      slut: kampanjSlutDatum(d.offer.planeradLeveransdag, d.config.campaignMonths),
      pilot: Number(d.config.campaignDiscount) >= 1,
    }));
  kandidater.sort((a, b) => b.slut.localeCompare(a.slut));
  return kandidater[0] || null;
}

/** "Pilot t.o.m. 2027-05-01" / "Kampanj t.o.m. …" */
export const kampanjText = k => (k ? `${k.pilot ? 'Pilot' : 'Kampanj'} t.o.m. ${k.slut}` : '');

/** Datum för de två påminnelserna: samtal 45 dagar före slutet, och på slutdagen. */
export function pilotPaminnelseDatum(slut) {
  const p = String(slut).split('-');
  const d = new Date(Number(p[0]), Number(p[1]) - 1, Number(p[2]), 12);
  d.setDate(d.getDate() - 45);
  const samtal = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  return { samtal, slut };
}

/**
 * Leadet en offert ska kopplas till: offertens kund matchas mot CRM-kunderna
 * (org.nr → namn → e-postdomän, första träffen som i analysexporten), och
 * kunden ska ha exakt ett Flatbox-lead. Samma regel för flatbox-link-leads
 * och den automatiska kopplingen i konfiguratorn.
 * @param off           offer{} (custOrg, custName, custEmail)
 * @param kunder        crmCompanies [{ id, name, orgNumber, website }]
 * @param flatboxLeads  Flatbox-leads [{ id, companyId, … }]
 * → { lead, kund, via } | { lead: null, orsak: 'ingen kund'|'inget Flatbox-lead'|'flera Flatbox-leads', kund, leads }
 */
export function leadForOffert(off, kunder, flatboxLeads) {
  const m = matchaFlatboxOffertKund(off, kunder);
  const kund = m.kandidater[0];
  if (!kund) return { lead: null, orsak: 'ingen kund', kund: null, leads: [], kandidater: m.kandidater };
  const leads = (flatboxLeads || []).filter(l => l.companyId === kund.id);
  if (leads.length !== 1) {
    return { lead: null, orsak: leads.length ? 'flera Flatbox-leads' : 'inget Flatbox-lead', kund, leads, kandidater: m.kandidater };
  }
  return { lead: leads[0], kund, via: m.via, kandidater: m.kandidater };
}
